// src/components/common/MediaSlider.js
import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getRenderableMediaItems } from "../../utils/seededPlaceMedia";

export function MediaSlider({
	items = [],            // default to empty array
	isLocked,
	mediaHeight = 240,
	onOpenLightbox,
	onMediaError,
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [mediaAspectRatios, setMediaAspectRatios] = useState({});
	const [hiddenFailedMediaKeys, setHiddenFailedMediaKeys] = useState({});
	const renderableItems = getRenderableMediaItems(items);
	const displayActiveIndex = Math.min(activeIndex, Math.max(renderableItems.length - 1, 0));

	// nothing to render if there are no media items
	if (renderableItems.length === 0) {
		return null;
	}

	const handleVideoPlay = (e) => {
		const videoEl = e.currentTarget;
		if (videoEl.requestFullscreen) {
			videoEl.requestFullscreen();
		} else if (videoEl.webkitEnterFullScreen) {
			videoEl.webkitEnterFullScreen();
		} else if (videoEl.msRequestFullscreen) {
			videoEl.msRequestFullscreen();
		}
	};

	return (
		<>
			<Box
				onScroll={(e) => {
					const el = e.currentTarget;
					if (!el?.children?.length) return;
					const viewportCenter = el.scrollLeft + el.clientWidth / 2;
					let closestIndex = 0;
					let closestDistance = Number.POSITIVE_INFINITY;

					Array.from(el.children).forEach((child, idx) => {
						const childCenter = child.offsetLeft + child.clientWidth / 2;
						const distance = Math.abs(childCenter - viewportCenter);
						if (distance < closestDistance) {
							closestDistance = distance;
							closestIndex = idx;
						}
					});

					setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
				}}
				sx={{
					display: "flex",
					overflowX: "auto",
					overflowY: "hidden",
					gap: 1,
					pb: 1,
					scrollSnapType: "x mandatory",
					WebkitOverflowScrolling: "touch",
					// Allow vertical drags to bubble to the page — the row only
					// scrolls horizontally (overflowY hidden), so "pan-x" alone
					// swallowed vertical scroll gestures that began on a thumbnail.
					touchAction: "pan-x pan-y",
					"&::-webkit-scrollbar": { display: "none" },
				}}
			>
				{renderableItems.map(({ item: m, originalIndex }, idx) => {
					const cardKey = `${m?.url || "media"}-${originalIndex}`;
					const cardAspectRatio = mediaAspectRatios[cardKey] || "4 / 3";

					return (
						<Box
							key={cardKey}
							sx={{
								flex: "0 0 auto",
								width: "auto",
								height: mediaHeight,
								aspectRatio: cardAspectRatio,
								borderRadius: 2,
								overflow: "hidden",
								position: "relative",
								scrollSnapAlign: "start",
							}}
						>
							{m.type === "image" ? (
								<img
									src={m.url}
									alt={`media-${originalIndex}`}
									onClick={() => !isLocked && onOpenLightbox?.(m, originalIndex)}
									onLoad={(e) => {
										const img = e.currentTarget;
										if (!img?.naturalWidth || !img?.naturalHeight) return;
										const nextAspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
										setMediaAspectRatios((prev) =>
											prev[cardKey] === nextAspectRatio
												? prev
												: { ...prev, [cardKey]: nextAspectRatio }
										);
										setHiddenFailedMediaKeys((prev) => {
											if (!prev[cardKey]) return prev;
											const next = { ...prev };
											delete next[cardKey];
											return next;
										});
									}}
									onError={() => {
										setHiddenFailedMediaKeys((prev) =>
											prev[cardKey] ? prev : { ...prev, [cardKey]: true }
										);
										onMediaError?.(m, originalIndex);
									}}
									style={{
										height: mediaHeight,
										width: "100%",
										objectFit: "contain",
										display: "block",
										visibility: hiddenFailedMediaKeys[cardKey] ? "hidden" : "visible",
										filter: isLocked ? "blur(6px)" : "none",
										pointerEvents: isLocked ? "none" : "auto",
										cursor: isLocked ? "default" : "pointer",
										transform: isLocked ? "scale(1.05)" : "none",
										transformOrigin: "center center",
										backgroundColor: "transparent",
									}}
								/>
							) : (
								<video
									src={m.url}
									controls={!isLocked}
									onPlay={(e) => !isLocked && handleVideoPlay(e)}
									onLoadedMetadata={(e) => {
										const video = e.currentTarget;
										if (!video?.videoWidth || !video?.videoHeight) return;
										const nextAspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
										setMediaAspectRatios((prev) =>
											prev[cardKey] === nextAspectRatio
												? prev
												: { ...prev, [cardKey]: nextAspectRatio }
										);
									}}
									style={{
										height: mediaHeight,
										width: "100%",
										objectFit: "contain",
										backgroundColor: "common.black",
										display: "block",
										filter: isLocked ? "blur(8px)" : "none",
										pointerEvents: isLocked ? "none" : "auto",
										cursor: isLocked ? "default" : "pointer",
									}}
								/>
							)}
						</Box>
					);
				})}
			</Box>

			{renderableItems.length > 1 && (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.8, mt: 0.25 }}>
					{renderableItems.map((_, idx) => (
						<Box
							key={`dot-${idx}`}
							sx={(theme) => ({
								width: idx === displayActiveIndex ? 18 : 8,
								height: 8,
								borderRadius: "999px",
								backgroundColor: idx === displayActiveIndex ? theme.colors.blue : alpha(theme.colors.blue, 0.25),
								transition: "all 160ms ease",
							})}
						/>
					))}
					<Typography variant="caption" sx={{ ml: 0.8, opacity: 0.8 }}>
						{displayActiveIndex + 1}/{renderableItems.length}
					</Typography>
				</Box>
			)}
		</>
	);
}
